/*
 * Render a Snap! project's custom-block definitions and top-level hatted
 * scripts to PNG data URLs inside
 * a snap-embed iframe (plans/snap-block-grid.md). The embed is same-origin
 * and exposes the real IDE_Morph as `window.snapIDE` (with every morphic
 * class on the same window), so this module reaches past snap-ide.ts's
 * narrow save/load slice into the deserializer and block morphs directly.
 *
 * The rendering is a browser-side port of the definition half of
 * scripts/snap-block-pics.ts's projectJs (which drives the identical code
 * through headless Chrome): setScale + refreshScale before the project
 * loads, rawOpenProjectString with error-catching off so a broken project
 * throws instead of half-loading, then each definition as the block
 * editor's view — a PrototypeHatBlockMorph with the definition body
 * attached, minus floating scratch scripts and the definition comment,
 * which have no layout outside a real editor. Keep the two in step —
 * except that this renderer skips definitions imported from a Snap!
 * library (below), which the batch exporter deliberately keeps.
 *
 * The grid feeds this blocks-only reductions (modules/snap-defs.ts server
 * side), but any loadable project XML works.
 *
 * Library blocks are left out: a project that imports, say, the iteration
 * library gains dozens of definitions the student didn't write, which
 * would swamp the overlay and make the grid's name filter match every
 * project. Our snap-importProvenance.patch (website/patches/) stamps each
 * imported definition's `origin` — "library:<file>" from the Libraries
 * dialog, "file:<name>" from a dropped/imported blocks file, bare "import"
 * when the name is unknown — and serializes it on <block-definition>, so
 * the reduction preserves it. Only `library:` definitions are dropped: a
 * block imported from a file is the student's own export (or a teacher's
 * handout), and stays. A block the student duplicates or authors has no
 * origin.
 */

export interface DefinitionPic {
  // A custom block's definition (the block editor's view), or a top-level
  // script under a hat block (when green flag clicked, when I receive …)
  // with everything attached to it.
  kind: 'definition' | 'script';
  owner: string; // 'global'/the stage, or the owning sprite's name
  spec: string; // definitions: the raw spec, slot markers included (foo %'n')
  name: string; // the filter/datalist key: label words, or the hat's label
  dataUrl: string;
  width: number; // the pic's pixel size, at the render scale
  height: number;
}

/*
 * A definition's display name: the spec's label words, slot tokens (%…)
 * dropped — the same naming scripts/snap-spec-names.ts uses for on-screen
 * block names.
 */
export const blockName = (spec: string): string =>
  spec
    .split(/\s+/)
    .filter((t) => t && !t.startsWith('%'))
    .join(' ');

// The slices of the embed window's morphic globals the renderer drives.
interface SnapDefinition {
  spec: string;
  body?: { expression?: SnapBlock | null } | null;
  // Provenance, from our importProvenance patch: null when authored in the
  // project, else "<kind>:<identifier>" (see the module comment).
  origin?: string | null;
}

// Imported from a Snap! library (the Libraries dialog), as opposed to
// authored here or imported from a file.
export const isLibraryImport = (def: { origin?: string | null }): boolean =>
  (def.origin ?? '').startsWith('library:');
interface SnapMorph {
  children: SnapMorph[];
  top(): number;
  left(): number;
}
interface SnapBlock extends SnapMorph {
  nextBlock(block: SnapBlock): void;
  fullCopy(): SnapBlock;
  fullImage(): HTMLCanvasElement;
}
interface SnapTarget {
  name: string;
  customBlocks: SnapDefinition[];
  scripts: { children: SnapMorph[] };
}
interface SnapStage extends SnapTarget {
  globalBlocks: SnapDefinition[];
}
interface SnapEmbedIDE {
  stage: SnapStage;
  sprites: { asArray(): SnapTarget[] };
  rawOpenProjectString(xml: string): void;
}
interface SnapEmbedWindow extends Window {
  snapReady?: Promise<unknown>;
  snapIDE?: unknown;
  world: { children: SnapEmbedIDE[] };
  SyntaxElementMorph: { prototype: { setScale(scale: number): void } };
  CommentMorph: { prototype: { refreshScale(): void } };
  Process: { prototype: { isCatchingErrors: boolean } };
  PrototypeHatBlockMorph: new (definition: SnapDefinition) => SnapBlock;
  // Distinct instance types so consecutive instanceof checks narrow
  // usefully (a shared instance type would leave `never` in else-branches).
  HatBlockMorph: new (
    ...args: never[]
  ) => SnapBlock; // CustomHatBlockMorph extends this too
  StringMorph: new (...args: never[]) => SnapMorph & { text: string };
  SymbolMorph: new (...args: never[]) => SnapMorph & { name: string };
  InputSlotMorph: new (...args: never[]) => SnapMorph & { contents(): { text?: string } };
}

// What a render produces: the pics, plus how many custom-block definitions
// were left out as library imports (so a view can say so).
export interface Rendering {
  defs: DefinitionPic[];
  libraryBlocks: number;
}

export interface SnapRenderer {
  // Load `xml` (replacing whatever the IDE holds) and return a pic of every
  // custom-block definition (global blocks first; library imports left
  // out and counted — see the module comment) and every top-level
  // hat-topped script (with its attached blocks), stage before sprites.
  // Throws on a project the deserializer can't load. `scale` is Snap!'s
  // block zoom, which it floors at 1 — render at 2 and shrink the <img> to
  // show blocks smaller than life size.
  definitionPics(xml: string, scale?: number): Rendering;
}

/*
 * Resolves once the embed in `frame` has booted (the same snapReady
 * handshake as snap-ide.ts's snapIDE()).
 */
export const snapRenderer = (frame: HTMLIFrameElement): Promise<SnapRenderer> =>
  new Promise((resolve) => {
    const ready = async () => {
      const w = frame.contentWindow as SnapEmbedWindow;
      await Promise.resolve(w.snapReady ?? w.snapIDE);
      resolve({
        definitionPics(xml: string, scale = 2): Rendering {
          // The same pair the IDE's zoom-blocks setting applies, before the
          // project (whose comments are built during deserialization) loads.
          w.SyntaxElementMorph.prototype.setScale(scale);
          w.CommentMorph.prototype.refreshScale();
          const ide = w.world.children[0];
          const wasCatching = w.Process.prototype.isCatchingErrors;
          w.Process.prototype.isCatchingErrors = false; // a load error should throw
          try {
            ide.rawOpenProjectString(xml);
          } finally {
            w.Process.prototype.isCatchingErrors = wasCatching;
          }
          const defs: DefinitionPic[] = [];
          let libraryBlocks = 0;
          const collect = (list: SnapDefinition[], owner: string) => {
            for (const def of list) {
              if (isLibraryImport(def)) {
                libraryBlocks++;
                continue;
              }
              const proto = new w.PrototypeHatBlockMorph(def);
              if (def.body?.expression) {
                proto.nextBlock(def.body.expression.fullCopy());
              }
              const image = proto.fullImage();
              defs.push({
                kind: 'definition',
                owner,
                spec: def.spec,
                name: blockName(def.spec),
                dataUrl: image.toDataURL('image/png'),
                width: image.width,
                height: image.height,
              });
            }
          };
          // A hat's on-screen label, for the filter/datalist: the label
          // words plus icon parts and simple slot values, so the entries
          // read the way the block does — "when green flag clicked",
          // "when I receive go", "when space key pressed".
          const hatLabel = (hat: SnapBlock): string => {
            const words: string[] = [];
            for (const part of hat.children) {
              if (part instanceof w.StringMorph && part.text) {
                words.push(part.text);
              } else if (part instanceof w.SymbolMorph) {
                words.push(part.name === 'flag' ? 'green flag' : part.name);
              } else if (part instanceof w.InputSlotMorph) {
                const text = part.contents().text;
                if (text) words.push(text);
              }
            }
            return words.filter(Boolean).join(' ') || 'script';
          };
          const collectScripts = (t: SnapTarget) => {
            const hats = t.scripts.children
              .filter((m): m is SnapBlock => m instanceof w.HatBlockMorph)
              .sort((a, b) => a.top() - b.top() || a.left() - b.left());
            for (const hat of hats) {
              const name = hatLabel(hat);
              const image = hat.fullImage();
              defs.push({
                kind: 'script',
                owner: t.name,
                spec: name,
                name,
                dataUrl: image.toDataURL('image/png'),
                width: image.width,
                height: image.height,
              });
            }
          };
          const stage = ide.stage;
          collect(stage.globalBlocks, 'global');
          const targets = [stage, ...ide.sprites.asArray()];
          for (const t of targets) {
            collect(t.customBlocks, t.name);
          }
          for (const t of targets) {
            collectScripts(t);
          }
          return { defs, libraryBlocks };
        },
      });
    };
    if ((frame.contentWindow as SnapEmbedWindow | null)?.snapReady) {
      void ready();
    } else {
      frame.addEventListener('load', () => void ready(), { once: true });
    }
  });
